import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { staffApi } from '@/lib/api';
import { Plus, Edit, Trash2, Users, X, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const AdminStaff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', title: '', bio: '', photo_url: '', photo_data: '', email: '', phone: '', order: 0
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const data = await staffApi.getAll();
      setStaff(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', title: '', bio: '', photo_url: '', photo_data: '', email: '', phone: '', order: staff.length });
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name, title: item.title, bio: item.bio,
      photo_url: item.photo_url || '', photo_data: item.photo_data || '',
      email: item.email || '', phone: item.phone || '', order: item.order || 0
    });
    setDialogOpen(true);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => setFormData({ ...formData, photo_data: e.target.result, photo_url: '' });
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.title || !formData.bio) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      if (editingItem) {
        await staffApi.update(editingItem.id, formData);
        toast.success('Staff updated');
      } else {
        await staffApi.create(formData);
        toast.success('Staff added');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await staffApi.delete(id);
      toast.success('Deleted');
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Staff Directory</h1>
          <p className="text-stone-600">Manage church leadership</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4 mr-2" /> Add Staff</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Staff' : 'Add Staff'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                </div>
              </div>
              <div>
                <Label>Bio *</Label>
                <Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} rows={4} required />
              </div>
              <div>
                <Label>Photo</Label>
                {formData.photo_data || formData.photo_url ? (
                  <div className="relative inline-block mt-2">
                    <img src={formData.photo_data || formData.photo_url} alt="Preview" className="h-24 w-24 rounded-full object-cover" />
                    <Button type="button" size="sm" variant="destructive" className="absolute -top-2 -right-2" onClick={() => setFormData({ ...formData, photo_data: '', photo_url: '' })}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <Input type="file" accept="image/*" onChange={handlePhotoUpload} className="mt-1" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
                <Button type="submit" className="bg-amber-700 hover:bg-amber-800">{editingItem ? 'Update' : 'Add'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <Card><CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto"></div>
        </CardContent></Card>
      ) : staff.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {staff.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <div className="aspect-square bg-stone-200">
                {item.photo_data || item.photo_url ? (
                  <img src={item.photo_data || item.photo_url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Users className="h-16 w-16 text-stone-400" /></div>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-stone-800">{item.name}</h3>
                <p className="text-amber-700 text-sm">{item.title}</p>
                <p className="text-stone-600 text-sm mt-2 line-clamp-2">{item.bio}</p>
                <div className="flex justify-end mt-4">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card><CardContent className="p-8 text-center">
          <Users className="h-12 w-12 text-stone-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-800 mb-2">No Staff Members</h3>
          <Button onClick={() => setDialogOpen(true)} className="bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4 mr-2" /> Add Staff</Button>
        </CardContent></Card>
      )}
    </div>
  );
};

export default AdminStaff;
